import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Shield, 
  TrendingUp, 
  Users, 
  FileText, 
  Settings,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  BarChart3,
  Wrench,
  ClipboardList,
  DollarSign,
  Key,
  AlertTriangle,
  Eye,
  Calculator,
  Briefcase,
  Factory,
  Warehouse,
  HardHat,
  Target,
  Clock,
  Handshake,
  Download,
  Cog,
  FileCheck,
  PieChart,
  Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// URL del PDF de presentación comercial
const PRESENTATION_PDF_URL = 'https://customer-assets.emergentagent.com/job_c2c3445d-7a1b-4eab-9b89-54adcdaf1551/artifacts/9ij1h5fu_NAVE.-2.pdf';

const Landing = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    mensaje: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    toast.success('Mensaje enviado. Nos pondremos en contacto pronto.');
    setContactForm({ nombre: '', empresa: '', email: '', telefono: '', mensaje: '' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleDownloadPDF = () => {
    window.open(PRESENTATION_PDF_URL, '_blank');
  };

  // Custom Logo Component with Image
  const NaveLogo = ({ className = "", iconOnly = false }) => (
    <div className={`nave-logo-wrapper ${className}`}>
      <img src="/naave-logo.png" alt="NAAVE" className="nave-logo-image" style={{ height: iconOnly ? '40px' : '50px' }} />
    </div>
  );

  return (
    <div className="landing-page">
      {/* Header */}
      <header className={`landing-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="landing-container">
          <div className="header-content">
            <NaveLogo className="header-logo-component" />

            {/* Desktop Navigation */}
            <nav className="header-nav desktop-nav">
              <button onClick={() => scrollToSection('nosotros')}>Nosotros</button>
              <button onClick={() => scrollToSection('clientes')}>Clientes</button>
              <button onClick={() => scrollToSection('servicios')}>Servicios</button>
              <button onClick={() => scrollToSection('parques')}>Parques</button>
              <button onClick={() => scrollToSection('contacto')}>Contacto</button>
            </nav>

            <Link to="/login" className="header-cta" data-testid="landing-login-btn">
              Ingresar al Sistema
            </Link>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="header-nav mobile-nav">
            <button onClick={() => scrollToSection('nosotros')}>Nosotros</button>
            <button onClick={() => scrollToSection('clientes')}>Clientes</button>
            <button onClick={() => scrollToSection('servicios')}>Servicios</button>
            <button onClick={() => scrollToSection('parques')}>Parques</button>
            <button onClick={() => scrollToSection('contacto')}>Contacto</button>
            <Link to="/login" className="mobile-cta">Ingresar al Sistema</Link>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1655936073069-07b2c9dc2db6?q=80&w=2070" 
            alt="Parque Industrial" 
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="landing-container">
          <div className="hero-content">
            <div className="hero-badge">
              <NaveLogo iconOnly className="hero-badge-icon" />
              <span>División de Parques Industriales</span>
            </div>
            <h1 className="hero-title" data-testid="hero-title">
              Administración Integral de<br />
              <span className="text-accent">Parques Industriales</span>
            </h1>
            <p className="hero-subtitle">
              Soluciones estratégicas, técnicas y financieras para maximizar el valor y la eficiencia de sus activos industriales.
            </p>
            <div className="hero-bullets">
              <div className="hero-bullet">
                <CheckCircle size={20} className="bullet-icon" />
                <span>Eficiencia Operativa</span>
              </div>
              <div className="hero-bullet">
                <CheckCircle size={20} className="bullet-icon" />
                <span>Control Financiero</span>
              </div>
              <div className="hero-bullet">
                <CheckCircle size={20} className="bullet-icon" />
                <span>Valor Patrimonial</span>
              </div>
            </div>
            <div className="hero-ctas">
              <button 
                className="btn-primary"
                onClick={() => scrollToSection('contacto')}
                data-testid="cta-administrar"
              >
                Solicitar Consulta
                <ArrowRight size={18} />
              </button>
              <button 
                className="btn-secondary"
                onClick={handleDownloadPDF}
                data-testid="download-pdf-btn"
              >
                <Download size={18} />
                Presentación Comercial
              </button>
            </div>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <span>Scroll</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="evolution-section" id="nosotros">
        <div className="landing-container">
          <div className="section-header">
            <span className="section-tag">Nosotros</span>
            <h2 className="section-title">Más de 30 años de experiencia</h2>
            <p className="section-description">
              En Grupo Inmobiliario Alarmen entendimos que los parques industriales no solo requieren mantenimiento, 
              sino una administración estratégica, técnica y financiera que garantice operación eficiente, 
              control presupuestal, cumplimiento de reglamentos, mantenimiento preventivo y relación con inquilinos.
            </p>
          </div>

          <div className="vision-card">
            <div className="vision-icon">
              <Target size={40} />
            </div>
            <div className="vision-content">
              <h3>Nuestra Visión</h3>
              <p>
                Convertirnos en el referente en administración de parques industriales en México, 
                elevando los estándares operativos a través de procesos claros, tecnología y gestión 
                orientada a resultados. Nuestro objetivo es que los parques operen con eficiencia, 
                transparencia y planeación estratégica, protegiendo el valor de los activos y potenciando 
                su rentabilidad a largo plazo.
              </p>
            </div>
          </div>

          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <div className="timeline-icon">
                  <Home size={32} />
                </div>
                <h3>Condominios Residenciales</h3>
                <p>30+ años administrando condominios y desarrollos verticales</p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div className="timeline-item">
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <div className="timeline-icon">
                  <Building2 size={32} />
                </div>
                <h3>Espacios Comerciales</h3>
                <p>Gestión de plazas comerciales y edificios corporativos</p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div className="timeline-item">
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <div className="timeline-icon">
                  <Factory size={32} />
                </div>
                <h3>Parques Industriales</h3>
                <p>Evolución natural hacia la administración industrial especializada</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="audience-section" id="clientes">
        <div className="landing-container">
          <div className="section-header">
            <span className="section-tag">Nuestros Clientes</span>
            <h2 className="section-title">¿A quién servimos?</h2>
          </div>

          <div className="audience-grid">
            <div className="audience-card">
              <div className="audience-icon">
                <HardHat size={36} />
              </div>
              <h3>Desarrolladores</h3>
              <p>
                Parques industriales que buscan estructurar su operación desde el inicio 
                con procesos claros, administración definida y control financiero profesional.
              </p>
            </div>
            <div className="audience-card">
              <div className="audience-icon">
                <Briefcase size={36} />
              </div>
              <h3>Propietarios y Fondos</h3>
              <p>
                Inversionistas que requieren transparencia, indicadores patrimoniales sólidos 
                y maximización del valor de sus activos industriales.
              </p>
            </div>
            <div className="audience-card">
              <div className="audience-icon">
                <Factory size={36} />
              </div>
              <h3>Inquilinos Industriales</h3>
              <p>
                Empresas que ocupan naves y requieren una administración profesional, 
                comunicación eficiente y atención oportuna para operar sin interrupciones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="servicios">
        <div className="landing-container">
          <div className="section-header">
            <span className="section-tag">Servicios</span>
            <h2 className="section-title">Administración Integral</h2>
            <p className="section-description">
              La operación de un parque industrial exige estructura, control y presencia constante. 
              Ofrecemos soluciones completas que abarcan desde la dirección institucional hasta el mantenimiento de bodegas.
            </p>
          </div>

          <div className="services-grid">
            {/* Park Administration */}
            <div className="service-card">
              <div className="service-header">
                <div className="service-icon">
                  <Factory size={28} />
                </div>
                <h3>Administración del Parque Industrial</h3>
                <p className="service-subtitle">Operación del Condominio</p>
              </div>
              <ul className="service-list">
                <li><Users size={18} /> Dirección y coordinación institucional</li>
                <li><DollarSign size={18} /> Control financiero y presupuestal</li>
                <li><Wrench size={18} /> Mantenimiento e infraestructura</li>
                <li><Shield size={18} /> Seguridad y operación</li>
                <li><Cog size={18} /> Gestión administrativa integral</li>
                <li><TrendingUp size={18} /> Planeación estratégica y mejora continua</li>
              </ul>
              <div className="service-details">
                <p>Organizamos asambleas, ejecutamos acuerdos, gestionamos cobranza, presupuestos anuales, CapEx/OpEx con reportes financieros claros.</p>
              </div>
            </div>

            {/* Property Management */}
            <div className="service-card">
              <div className="service-header">
                <div className="service-icon accent">
                  <Warehouse size={28} />
                </div>
                <h3>Administración Patrimonial</h3>
                <p className="service-subtitle">Gestión de Activos</p>
              </div>
              <ul className="service-list">
                <li><FileCheck size={18} /> Gestión contractual estratégica</li>
                <li><DollarSign size={18} /> Cobranza y control de flujos</li>
                <li><Target size={18} /> Optimización de ocupación</li>
                <li><PieChart size={18} /> Indicadores y análisis patrimonial</li>
                <li><Handshake size={18} /> Atención a inquilinos</li>
                <li><Wrench size={18} /> Mantenimiento de bodegas</li>
              </ul>
              <div className="service-details">
                <p>Estructuramos contratos, supervisamos cobranza de rentas, desarrollamos estrategias para reducir vacancia y evaluamos rentabilidad del activo.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parks Section */}
      <section className="parks-section" id="parques">
        <div className="landing-container">
          <div className="section-header">
            <span className="section-tag">Experiencia</span>
            <h2 className="section-title">Parques Administrados</h2>
            <p className="section-description">
              Experiencia real en operación industrial
            </p>
          </div>

          <div className="parks-grid">
            {[
              { name: 'Flexpark El Salto', location: 'El Salto, Jalisco' },
              { name: 'Quality Park', location: 'Guadalajara, Jalisco' },
              { name: 'Parque Siete', location: 'Zapopan, Jalisco' },
              { name: 'Flexpark Las Cuatas', location: 'Tlaquepaque, Jalisco' },
              { name: 'Flexpark Toluquilla', location: 'Tlaquepaque, Jalisco' },
              { name: 'Parque Industrial Montenegro', location: 'El Salto, Jalisco' },
            ].map((park) => (
              <div key={park.name} className="park-card">
                <div className="park-icon">
                  <Factory size={24} />
                </div>
                <div className="park-info">
                  <h4>{park.name}</h4>
                  <p>{park.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="differentiators-section">
        <div className="landing-container">
          <div className="section-header">
            <span className="section-tag">Diferenciadores</span>
            <h2 className="section-title">¿Por qué NAAVE?</h2>
          </div>

          <div className="diff-grid">
            {[
              { icon: Clock, title: '30+ años', desc: 'en administración profesional' },
              { icon: Settings, title: 'Modelo híbrido', desc: 'condominio + patrimonial' },
              { icon: Shield, title: 'Continuidad operativa', desc: 'sin interrupciones' },
              { icon: Users, title: 'Gobernanza industrial', desc: 'estructurada y transparente' },
              { icon: Eye, title: 'Supervisión en campo', desc: 'presencia física constante' },
              { icon: TrendingUp, title: 'Ocupación + rendimiento', desc: 'maximización del valor' },
              { icon: BarChart3, title: 'KPIs y reporting', desc: 'datos en tiempo real' },
              { icon: Handshake, title: 'Transparencia', desc: 'comunicación continua' },
            ].map((item) => (
              <div key={item.title} className="diff-item">
                <div className="diff-icon">
                  <item.icon size={24} />
                </div>
                <div className="diff-text">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="contacto">
        <div className="landing-container">
          <div className="cta-content">
            <div className="cta-text">
              <span className="section-tag light">Comencemos</span>
              <h2>Presentamos NAAVE</h2>
              <p>
                Agenda una presentación personalizada para conocer cómo podemos 
                administrar tu parque industrial de manera profesional.
              </p>
              <div className="cta-buttons">
                <button className="btn-accent" onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}>
                  Agendar Presentación
                  <ArrowRight size={18} />
                </button>
                <button className="btn-outline-light" onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}>
                  Cotizar Servicios
                </button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="cta-form" id="contact-form">
              <h3>Contáctanos</h3>
              <form onSubmit={handleContactSubmit}>
                <div className="form-row">
                  <Input
                    placeholder="Nombre completo"
                    value={contactForm.nombre}
                    onChange={(e) => setContactForm({...contactForm, nombre: e.target.value})}
                    required
                    className="landing-input"
                  />
                  <Input
                    placeholder="Empresa / Parque"
                    value={contactForm.empresa}
                    onChange={(e) => setContactForm({...contactForm, empresa: e.target.value})}
                    className="landing-input"
                  />
                </div>
                <div className="form-row">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                    className="landing-input"
                  />
                  <Input
                    placeholder="Teléfono"
                    value={contactForm.telefono}
                    onChange={(e) => setContactForm({...contactForm, telefono: e.target.value})}
                    className="landing-input"
                  />
                </div>
                <Textarea
                  placeholder="¿Cómo podemos ayudarte?"
                  value={contactForm.mensaje}
                  onChange={(e) => setContactForm({...contactForm, mensaje: e.target.value})}
                  rows={4}
                  className="landing-input"
                />
                <Button type="submit" className="submit-btn">
                  Enviar Mensaje
                  <ChevronRight size={18} />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo-wrapper">
                <img src="/naave-logo.png" alt="NAAVE" style={{ height: '45px' }} />
              </div>
              <p className="footer-desc">
                Administración profesional de parques industriales y gestión patrimonial de bodegas.
              </p>
            </div>

            <div className="footer-links">
              <h4>Navegación</h4>
              <button onClick={() => scrollToSection('nosotros')}>Nosotros</button>
              <button onClick={() => scrollToSection('clientes')}>Clientes</button>
              <button onClick={() => scrollToSection('servicios')}>Servicios</button>
              <button onClick={() => scrollToSection('parques')}>Parques</button>
              <button onClick={() => scrollToSection('contacto')}>Contacto</button>
            </div>

            <div className="footer-contact">
              <h4>Contacto</h4>
              <div className="contact-item">
                <Phone size={16} />
                <span>333 191 2529</span>
              </div>
              <div className="contact-item">
                <Mail size={16} />
                <span>@nave_mx</span>
              </div>
              <div className="contact-item">
                <MapPin size={16} />
                <a href="https://www.naave.mx" target="_blank" rel="noopener noreferrer">www.naave.mx</a>
              </div>
            </div>

            <div className="footer-system">
              <h4>Sistema</h4>
              <Link to="/login" className="footer-system-btn">
                Ingresar al Sistema
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} NAAVE. Todos los derechos reservados.</p>
            <div className="footer-legal">
              <a href="#">Aviso de Privacidad</a>
              <a href="#">Términos y Condiciones</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
